"use client";

import { useState } from "react";
import type { YouTubeVideo } from "@/server/modules/youtube";

/**
 * The channel's newest videos, in landscape.
 *
 * Nothing from YouTube loads until a card is clicked: each one is the poster
 * image and a play button, and only then is the player put in its place. An
 * iframe per video would pull in YouTube's player and its cookies on every
 * homepage view, for videos most visitors never start.
 *
 * youtube-nocookie.com is YouTube's own no-tracking-until-play host, and the
 * card is a <button>, so it is reachable by keyboard like any other control.
 */
function VideoCard({ video }: { video: YouTubeVideo }) {
  const [playing, setPlaying] = useState(false);

  // A reel's size exactly (--reel-w × --reel-h), so the two YouTube cards
  // line up with the two rows of reels: a portrait thumbnail that fills what
  // the title leaves. The 16:9 poster is cropped to fit (object-cover), never
  // stretched; once playing, YouTube's player letterboxes the film itself.
  return (
    <figure className="m-0 flex h-[var(--reel-h,196px)] w-[var(--reel-w,104px)] flex-col">
      <div className="relative min-h-0 flex-1 overflow-hidden bg-[var(--border-soft)]">
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play: ${video.title}`}
            className="group absolute inset-0 cursor-pointer border-0 bg-transparent p-0"
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- YouTube's
                poster host, kept out of the image optimiser: these are remote
                thumbnails that change whenever the channel re-uploads. */}
            <img
              src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <span className="absolute inset-0 bg-[rgba(3,12,9,0.25)] transition-colors group-hover:bg-[rgba(3,12,9,0.1)]" />
            <span className="absolute top-1/2 left-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(7,17,13,0.82)] transition-transform group-hover:scale-110">
              <svg width="11" height="12" viewBox="0 0 20 22" fill="none" aria-hidden>
                <path d="M19 11 1 21.4V.6L19 11Z" fill="var(--on-dark)" />
              </svg>
            </span>
          </button>
        )}
      </div>
      {/* Two lines at most, so one long title cannot make its card taller
          than the others in the column. A video entered by hand in the CMS
          arrives without a title, and then there is no caption at all. */}
      {/* Always two lines tall, even for a short or missing title, so both
          cards keep the reels' height. */}
      <figcaption className="mt-1.5 line-clamp-2 h-7 shrink-0 text-[11px] leading-[14px] font-semibold text-[var(--foreground)]">
        {video.title}
      </figcaption>
    </figure>
  );
}

/**
 * The videos, without a heading — the social wall owns those.
 *
 * Stacked beside the reels (768px and up), one per row, with the reels' gap —
 * each card is a reel's size, so the rows line up. Two across under the reels
 * on a phone.
 */
export function YouTubeVideoColumn({ videos }: { videos: YouTubeVideo[] }) {
  if (videos.length === 0) return null;
  return (
    <div className="grid grid-cols-[repeat(2,var(--reel-w,104px))] justify-center gap-[var(--reel-gap,16px)] md:grid-cols-[var(--reel-w,104px)]">
      {videos.map((video) => (
        <VideoCard key={video.id} video={video} />
      ))}
    </div>
  );
}

/**
 * One YouTube video for the social section's YouTube card. A Short fills a
 * tall 9:16 frame (poster cropped to the centre, never stretched), level with
 * the Instagram and Facebook cards; a long video keeps its 16:9 shape with
 * its full title beneath, in a card that ends with its content (owner,
 * 26 Sept 2026). YouTube itself loads only on click, from
 * youtube-nocookie.com.
 */
export function YouTubeCard({ video, short = false }: { video: YouTubeVideo; short?: boolean }) {
  const [playing, setPlaying] = useState(false);
  const poster = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;

  const player = playing ? (
    <iframe
      src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
      title={video.title}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      className="absolute inset-0 h-full w-full border-0"
    />
  ) : (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={`Play: ${video.title}`}
      className="group absolute inset-0 cursor-pointer border-0 bg-transparent p-0"
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- YouTube's own poster host */}
      <img src={poster} alt="" loading="lazy" className="h-full w-full object-cover" />
      <span className="absolute inset-0 bg-[rgba(3,12,9,0.2)] transition-colors group-hover:bg-transparent" />
      <span className="absolute top-1/2 left-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(7,17,13,0.82)] transition-transform group-hover:scale-110">
        <svg width="17" height="19" viewBox="0 0 20 22" fill="none" aria-hidden>
          <path d="M19 11 1 21.4V.6L19 11Z" fill="var(--on-dark)" />
        </svg>
      </span>
    </button>
  );

  return (
    <div>
      <div className={`relative w-full overflow-hidden bg-black ${short ? "aspect-[9/16]" : "aspect-video"}`}>
        {player}
      </div>
      {video.title ? (
        <p
          className={`m-0 px-4 pt-3 text-[14px] leading-snug font-semibold text-[var(--foreground)] ${
            short ? "line-clamp-2" : ""
          }`}
        >
          {video.title}
        </p>
      ) : null}
    </div>
  );
}
